import React, { useState, useContext, useEffect, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SocialIconButton from "../components/SocialIconButton";
import { FaLinkedin, FaInstagram, FaYoutube, FaGithub, FaStackOverflow } from "react-icons/fa";
import { socialMediaLinks, logoBackPath, loginName, telNummer, mail } from "../config";
import { NameContext } from "../context/NameContext";
import UnderlineSketch from "./UnderlineSketch";
import { useAuth } from "../auth/AuthContext";
import { MonoButtonSmall, PaperGradientBG } from "../kits/DesignKit";

import { useCourses } from "../hooks/useCourses";
import { useBlogPosts } from "../hooks/useBlogPosts";
import SearchOverlay from "../components/SearchOverlay";

import FaSearchAni from "./utils/FaSearchAni";
import { MonoButton } from "../kits";

const BookingModal = lazy(() => import("./BookingModal"));

// Sekundär-Navigation für verschiedene Seiten
const pageAnchors = {
  // About-Navigation wurde ins UserOverlay verschoben
  // "/about": {
  //   tabs: [
  //     { id: "overview", label: "Übersicht" },
  //     { id: "kompass", label: "Kompass" },
  //     { id: "referenzen", label: "Referenzen" }
  //   ],
  //   useQuery: false // verwendet #anchor
  // }
};

function Navbar({ updateNavbarHeight }) {
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const { name } = useContext(NameContext);
  const location = useLocation();
  const navbarRef = useRef(null);
  const scrollPosition = useRef(0);

  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [, setShowTopBar] = useState(true);

  const { courses } = useCourses();
  const { blogPosts } = useBlogPosts();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ courses: [], blog: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isDesktopSearchOverlayOpen, setIsDesktopSearchOverlayOpen] = useState(false);
  const [searchOverlayInitialView, setSearchOverlayInitialView] = useState("search");

  const lastScrollY = useRef(0);
  const [showBooking, setShowBooking] = useState(false);

  const navLinks = [
    { name: name ? `Moin ${name}` : "Moin", path: "/", isGreeting: true },
    { name: "Ich", path: "/about" },
    { name: "ZenPostStudio", path: "/zenpost-studio" },
    
    { name: "ZenLab", path: "/zenlab2" },
    // { name: "Kurse", path: "/courses" },
    { name: "SagHallo", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < lastScrollY.current) {
        setShowTopBar(true);
      } else {
        setShowTopBar(false);
      }
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toSearchText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) return value.map(toSearchText).join(" ");
    if (typeof value === "object") return Object.values(value).map(toSearchText).join(" ");
    return "";
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    const query = toSearchText(value).trim().toLowerCase();

    if (query.length > 0) {
      const courseResults = courses.filter((course) =>
        `${toSearchText(course?.title)} ${toSearchText(course?.description)} ${toSearchText(course?.instructor)}`
          .toLowerCase()
          .includes(query)
      );

      const blogResults = blogPosts.filter((b) =>
        `${toSearchText(b?.title)} ${toSearchText(b?.content)} ${toSearchText(b?.excerpt)}`
          .toLowerCase()
          .includes(query)
      );

      setSearchResults({
        courses: courseResults.slice(0, 5),
        blog: blogResults.slice(0, 5),
      });
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  useEffect(() => {
    if (!navbarRef.current || !updateNavbarHeight) return;
    const node = navbarRef.current;
    const syncHeight = () => {
      const height = node.offsetHeight;
      updateNavbarHeight(height);
      document.documentElement.style.setProperty("--navbar-height", `${height}px`);
    };
    syncHeight();
    const observer = new ResizeObserver(() => syncHeight());
    observer.observe(node);
    return () => observer.disconnect();
  }, [updateNavbarHeight]);

  useEffect(() => {
    if (!isMobileSearchOpen) return;
    scrollPosition.current = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollPosition.current}px`;
    document.body.style.width = "100%";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.position = "relative";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.position = "";
      window.scrollTo(0, scrollPosition.current);
    };
  }, [isMobileSearchOpen]);

  return (
    <header
      ref={navbarRef}
      className="fixed top-[0] w-full 
      z-[80] border-b border-gray-400/50
       bg-[#d9d4c5] shadow-md"
    >
      {/* Top Bar */}
      <PaperGradientBG 
      variant="zenBlack"
      className="py-2 text-[#EFEBDC] hidden lg:flex justify-between px-4">
        <div className="flex items-center space-x-4 px-36">
          <a href={socialMediaLinks.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaLinkedin size={15}/></a>
          <a href={socialMediaLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaInstagram size={15}/></a>
          <a href={socialMediaLinks.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaYoutube size={15}/></a>
          <a href={socialMediaLinks.github} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaGithub size={15}/></a>
          <a href={socialMediaLinks.stack} target="_blank" rel="noopener noreferrer" className="hover:text-brand"><FaStackOverflow size={15}/></a>
        </div>

        <div className="flex items-center space-x-4 text-xs hover:text-brand">
          <button onClick={() => setShowBooking(true)}>lern mich kennen</button>
          {showBooking && (
            <Suspense fallback={null}>
              <BookingModal onClose={() => setShowBooking(false)} />
            </Suspense>
          )}
        </div>

        <div className="flex items-center space-x-4 text-xs font-mono">
          <a href={`mailto:${mail.value}`} className="mt-1 hidden sm:block hover:text-brand">
            {mail.text}
          </a>
          <a href={`tel:${telNummer.value}`} className="mt-1 hidden sm:block hover:text-brand">
            {telNummer.text}
          </a>
          {token ? (
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
            >
              <MonoButtonSmall className="text-xs bg-brand text-gray-900 h-5">Log Out</MonoButtonSmall>
            </button>
          ) : (
            <Link to="/login">
              <MonoButtonSmall className="text-xs bg-brand h-5">{loginName.text}</MonoButtonSmall>
            </Link>
          )}
        </div>
      </PaperGradientBG>

      {/* Main Navbar */}
      <nav className="font-mono">
        <div className="container mx-auto flex justify-between items-center px-4 py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <Link
              to="/"
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center hidden lg:flex border border-brand/40 bg-transparent"
              aria-label="Zur Startseite"
            >
              <img
                src={logoBackPath}
                alt="Logo"
                className="w-full h-full rounded-full object-contain"
                width="512"
                height="512"
                decoding="async"
              />
            </Link>
<span
  className="
    hidden                 /* Standard: versteckt */
    lg:inline-block        /* erst ab lg sichtbar */
    font-mono text-xs text-gray-900 whitespace-nowrap
    overflow-hidden text-ellipsis
    lg:max-w-none
  "
>
  Software Full-Stack Engineering
</span>
            {!isMobileSearchOpen && (
              <div className="flex items-center space-x-3 text-[#171717] lg:hidden">
                <SocialIconButton href={socialMediaLinks.linkedin} Icon={FaLinkedin} />
                <SocialIconButton href={socialMediaLinks.youtube} Icon={FaYoutube} />
                <SocialIconButton href={socialMediaLinks.instagram} Icon={FaInstagram} />
                <SocialIconButton href={socialMediaLinks.github} Icon={FaGithub} />
                <SocialIconButton href={socialMediaLinks.stack} Icon={FaStackOverflow} />
              </div>
            )}
          </div>

          {/* Desktop Links */}
          <div className="hidden xl:flex items-center space-x-6">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-xs group ${isActive ? "text-black" : "text-gray-800 hover:text-black"}`}
                >
                  {link.name}
                  <span className="absolute inset-x-0 -bottom-3 h-[8px] pointer-events-none">
                    <div
                      className={`w-full transition-transform duration-500 ease-in-out origin-left transform ${
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    >
                      <UnderlineSketch color={isActive ? "black" : "#AC8E66"} />
                    </div>
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Desktop Search */}
          <div className="relative hidden lg:block ml-6">
            <input
              type="text"
              placeholder="Wie kann ich Dir helfen?"
              value={searchQuery}
              onChange={handleSearchChange}
              className="border rounded-md bg-[#d0cbb8] py-1 px-3 w-64 lg:w-72 focus:outline-none focus:ring-1 focus:ring-gray-800 text-xs"
            />
            {showSearchResults && (
              <div className="absolute mt-2 w-full bg-[#f0ede1] border border-gray-800 shadow-lg rounded-md z-50">
                {/* Kurse */}
                {searchResults.courses.length > 0 && (
                  <>
                    <div className="px-4 py-1 text-xs font-bold text-[#AC8E66]">&gt;&nbsp; Kurse</div>
                    {searchResults.courses.map((course) => (
                      <Link
                        key={course.id}
                        to={`/course/${course.slug}`}
                        className="block px-4 py-2 hover:bg-brand text-sm"
                        onClick={() => setShowSearchResults(false)}
                      >
                        {course.title}
                      </Link>
                    ))}
                  </>
                )}
                {/* Blog */}
                {searchResults.blog.length > 0 && (
                  <>
                    <div className="px-4 py-1 text-xs font-bold text-gray-600">Blog</div>
                    {searchResults.blog.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="block px-4 py-2 hover:bg-brand text-sm"
                        onClick={() => setShowSearchResults(false)}
                      >
                        {post.title}
                      </Link>
                    ))}
                  </>
                )}
                {/* Nichts gefunden */}
                {searchResults.courses.length === 0 && searchResults.blog.length === 0 && (
                  <MonoButton
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchOverlayInitialView("request");
                      setIsDesktopSearchOverlayOpen(true);
                    }}
                    className="cursor-pointer px-4 py-2 text-xs"
                  >
                    Nichts gefunden? Frage mich an!
                  </MonoButton>
                )}
              </div>
            )}
          </div>

          {/* Mobile Icons */}
          <div className="lg:hidden flex items-center">
            {!isMobileSearchOpen && (
              <button
                onClick={() => {
                  setSearchOverlayInitialView("search");
                  setIsMobileSearchOpen(true);
                }}
                className="text-gray-800 mr-4 pt-1"
              >
                <FaSearchAni />
              </button>
            )}
          </div>
        </div>
      </nav>

      {(isMobileSearchOpen || isDesktopSearchOverlayOpen) && (
        <SearchOverlay
          initialView={searchOverlayInitialView}
          searchQuery={searchQuery}
          filteredCourses={searchResults.courses}
          filteredBlog={searchResults.blog}
          onSearchChange={handleSearchChange}
          onResultClick={() => setShowSearchResults(false)}
          onClose={() => {
            setIsMobileSearchOpen(false);
            setIsDesktopSearchOverlayOpen(false);
            setSearchOverlayInitialView("search");
          }}
        />
      )}

      {/* Sekundär-Navigation für Seiten mit Ankern */}
      {pageAnchors[location.pathname] && (() => {
        const config = pageAnchors[location.pathname];
        const isLernstudio = config.useQuery;

        return (
          <div className="w-full bg-[#efefef] border-b border-gray-300 py-2 shadow-md">
            <div className="container mx-auto px-3">
              <div className="flex flex-col md:flex-row md:flex-wrap justify-center items-center gap-2 font-mono text-xs">
              {config.tabs.map((item) => {
                // Für Lernstudio (useQuery: true)
                if (isLernstudio) {
                  const params = new URLSearchParams(location.search);
                  const activeTab = params.get("tab") || "zendev";
                  const isActive = activeTab === item;

                  return (
                    <Link key={item} to={`${location.pathname}?tab=${item}`} className="w-full md:w-auto">
                      <MonoButton
                        className={`w-full py-2 transition text-center ${
                          isActive
                            ? " bg-[#151515] text-orange-50 text-shadow"
                            : "text-gray-950"
                        }`}
                      >
                        {item.charAt(0).toUpperCase() + item.slice(1)}
                      </MonoButton>
                    </Link>
                  );
                }

                // Für andere Seiten (useQuery: false) - Anchor Links
                const handleAnchorClick = (e) => {
                  e.preventDefault();
                  const element = document.getElementById(item.id);
                  if (element) {
                    const navbarHeight = navbarRef.current?.offsetHeight || 120;
                    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                    const offsetPosition = elementPosition - navbarHeight - 20;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                };

                // Prüfe ob dieser Anker aktuell sichtbar ist (für active State)
                const isActive = location.hash === `#${item.id}`;

                return (
                  <MonoButton
                    key={item.id}
                    onClick={handleAnchorClick}
                    className={`w-full md:w-auto py-2 transition text-center ${
                      isActive
                        ? " bg-[#151515] text-orange-50 text-shadow"
                        : "text-gray-950"
                    }`}
                  >
                    {item.label}
                  </MonoButton>
                );
              })}
              </div>
            </div>
          </div>
        );
      })()}
    </header>
  );
}

export default Navbar;
